import { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Search, Shield, Clock, ChevronLeft, ChevronRight, Activity } from 'lucide-react';

const ACTION_COLORS = {
  login_success: 'bg-emerald-50 text-emerald-700',
  login_failed: 'bg-red-50 text-red-600',
  bid_created: 'bg-sky-50 text-sky-700',
  bid_updated: 'bg-sky-50 text-sky-700',
  bid_status_changed: 'bg-violet-50 text-violet-700',
  bid_unlocked: 'bg-amber-50 text-amber-700',
  line_item_added: 'bg-sky-50 text-sky-600',
  line_item_removed: 'bg-red-50 text-red-600',
  lead_created: 'bg-emerald-50 text-emerald-700',
  lead_updated: 'bg-emerald-50 text-emerald-700',
  lead_status_changed: 'bg-violet-50 text-violet-700',
  lead_converted_to_bid: 'bg-violet-50 text-violet-700',
  proposal_sent: 'bg-amber-50 text-amber-700',
  proposal_viewed: 'bg-sky-50 text-sky-700',
  proposal_signed: 'bg-emerald-50 text-emerald-700',
  plan_created: 'bg-sky-50 text-sky-700',
  plan_updated: 'bg-sky-50 text-sky-700',
  plan_status_changed: 'bg-violet-50 text-violet-700',
  project_created: 'bg-emerald-50 text-emerald-700',
  change_order_created: 'bg-amber-50 text-amber-700',
  user_created: 'bg-violet-50 text-violet-700',
};

function timeAgo(ts) {
  const diff = (Date.now() - new Date(ts).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(ts).toLocaleDateString();
}

export default function AuditLog() {
  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [stats, setStats] = useState({});
  const [entityTypes, setEntityTypes] = useState([]);
  const [actionTypes, setActionTypes] = useState([]);
  const [filters, setFilters] = useState({ entity_type: '', action: '', search: '', days: 30 });
  const [selectedEvent, setSelectedEvent] = useState(null);
  const limit = 30;

  const loadEvents = useCallback(async () => {
    const params = new URLSearchParams({ page: page.toString(), limit: limit.toString(), days: filters.days.toString() });
    if (filters.entity_type) params.set('entity_type', filters.entity_type);
    if (filters.action) params.set('action', filters.action);
    if (filters.search) params.set('search', filters.search);
    const r = await api.get(`/audit?${params}`);
    setEvents(r.data.events || []);
    setTotal(r.data.total || 0);
  }, [page, filters]);

  useEffect(() => { loadEvents(); }, [loadEvents]);
  useEffect(() => {
    api.get('/audit/stats').then(r => setStats(r.data)).catch(() => {});
    api.get('/audit/entity-types').then(r => setEntityTypes(r.data)).catch(() => {});
    api.get('/audit/action-types').then(r => setActionTypes(r.data)).catch(() => {});
  }, []);

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6" data-testid="audit-log">
      {/* Stats Bar */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="border-slate-100">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-[#0F1D32] font-mono">{stats.today || 0}</p>
            <p className="text-xs text-slate-500">Events Today</p>
          </CardContent>
        </Card>
        <Card className="border-slate-100">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-[#0F1D32] font-mono">{stats.week || 0}</p>
            <p className="text-xs text-slate-500">This Week</p>
          </CardContent>
        </Card>
        <Card className="border-slate-100">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-[#0F1D32] font-mono">{stats.total || 0}</p>
            <p className="text-xs text-slate-500">All Time</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="border-slate-100">
        <CardContent className="p-4">
          <div className="flex gap-3 items-end flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search events..."
                  value={filters.search}
                  onChange={e => { setFilters(f => ({...f, search: e.target.value})); setPage(1); }}
                  className="pl-9 h-9"
                  data-testid="audit-search"
                />
              </div>
            </div>
            <Select value={filters.entity_type || 'all'} onValueChange={v => { setFilters(f => ({...f, entity_type: v === 'all' ? '' : v})); setPage(1); }}>
              <SelectTrigger className="w-36 h-9" data-testid="audit-entity-filter"><SelectValue placeholder="Entity" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Entities</SelectItem>
                {entityTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filters.action || 'all'} onValueChange={v => { setFilters(f => ({...f, action: v === 'all' ? '' : v})); setPage(1); }}>
              <SelectTrigger className="w-44 h-9" data-testid="audit-action-filter"><SelectValue placeholder="Action" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                {actionTypes.map(a => <SelectItem key={a} value={a}>{a.replace(/_/g, ' ')}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filters.days.toString()} onValueChange={v => { setFilters(f => ({...f, days: parseInt(v)})); setPage(1); }}>
              <SelectTrigger className="w-28 h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1">24 hours</SelectItem>
                <SelectItem value="7">7 days</SelectItem>
                <SelectItem value="30">30 days</SelectItem>
                <SelectItem value="90">90 days</SelectItem>
                <SelectItem value="365">1 year</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Events List */}
      <Card className="border-slate-100">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Shield className="w-4 h-4 text-[#F59E0B]" /> Audit Trail
              <span className="text-xs text-slate-400 font-normal">({total} events)</span>
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="audit-prev">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-xs text-slate-500">{page} / {totalPages || 1}</span>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="audit-next">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {events.length === 0 ? (
            <div className="p-8 text-center text-slate-400">
              <Activity className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No audit events found</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {events.map(evt => (
                <div
                  key={evt.id}
                  className="px-4 py-3 hover:bg-slate-50 cursor-pointer transition-colors flex items-center gap-4"
                  onClick={() => setSelectedEvent(evt)}
                  data-testid={`audit-event-${evt.id}`}
                >
                  <div className="w-16 text-right flex-shrink-0">
                    <p className="text-[10px] text-slate-400 font-mono">{timeAgo(evt.timestamp)}</p>
                  </div>
                  <div className="flex-shrink-0">
                    <Badge className={`text-[9px] px-1.5 ${ACTION_COLORS[evt.action] || 'bg-slate-100 text-slate-600'}`}>
                      {evt.action?.replace(/_/g, ' ')}
                    </Badge>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-800 truncate">{evt.details?.description || evt.action}</p>
                    <p className="text-[10px] text-slate-400 truncate">
                      {evt.entity_display || evt.entity_type} {evt.entity_id ? `(${evt.entity_id.slice(0,8)}...)` : ''}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-xs text-slate-600">{evt.actor_name}</p>
                    <p className="text-[10px] text-slate-400">{evt.actor_role}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={!!selectedEvent} onOpenChange={() => setSelectedEvent(null)}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="audit-detail-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">Audit Event Detail</DialogTitle>
          </DialogHeader>
          {selectedEvent && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div><p className="text-[10px] text-slate-400 uppercase">Timestamp</p><p className="font-mono text-xs">{new Date(selectedEvent.timestamp).toLocaleString()}</p></div>
                <div><p className="text-[10px] text-slate-400 uppercase">Action</p><Badge className={`${ACTION_COLORS[selectedEvent.action] || 'bg-slate-100 text-slate-600'}`}>{selectedEvent.action?.replace(/_/g, ' ')}</Badge></div>
                <div><p className="text-[10px] text-slate-400 uppercase">Actor</p><p>{selectedEvent.actor_name} <span className="text-slate-400">({selectedEvent.actor_role})</span></p></div>
                <div><p className="text-[10px] text-slate-400 uppercase">Entity</p><p>{selectedEvent.entity_type}: {selectedEvent.entity_display}</p></div>
              </div>
              <div>
                <p className="text-[10px] text-slate-400 uppercase mb-1">Description</p>
                <p className="text-slate-700">{selectedEvent.details?.description}</p>
              </div>
              {selectedEvent.details?.changed_fields?.length > 0 && (
                <div>
                  <p className="text-[10px] text-slate-400 uppercase mb-1">Changed Fields</p>
                  <div className="bg-slate-50 rounded-lg p-3 space-y-2">
                    {selectedEvent.details.changed_fields.map((cf, i) => (
                      <div key={i} className="flex items-start gap-2 text-xs">
                        <span className="font-mono font-semibold text-slate-700 min-w-[100px]">{cf.field}</span>
                        <span className="text-red-500 line-through">{typeof cf.old_value === 'object' ? JSON.stringify(cf.old_value) : String(cf.old_value ?? '—')}</span>
                        <span className="text-slate-400">→</span>
                        <span className="text-emerald-600">{typeof cf.new_value === 'object' ? JSON.stringify(cf.new_value) : String(cf.new_value ?? '—')}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3 pt-2 border-t border-slate-100">
                <div><p className="text-[10px] text-slate-400 uppercase">Source</p><p className="text-xs">{selectedEvent.context?.source || '—'}</p></div>
                <div><p className="text-[10px] text-slate-400 uppercase">IP</p><p className="text-xs font-mono">{selectedEvent.context?.ip || '—'}</p></div>
                <div><p className="text-[10px] text-slate-400 uppercase">Event ID</p><p className="text-[10px] font-mono text-slate-400">{selectedEvent.id}</p></div>
                <div><p className="text-[10px] text-slate-400 uppercase">Entity ID</p><p className="text-[10px] font-mono text-slate-400">{selectedEvent.entity_id}</p></div>
              </div>
              {selectedEvent.details && Object.keys(selectedEvent.details).filter(k => !['description', 'changed_fields'].includes(k)).length > 0 && (
                <div>
                  <p className="text-[10px] text-slate-400 uppercase mb-1">Extra Metadata</p>
                  <pre className="bg-slate-50 rounded-lg p-3 text-[10px] font-mono overflow-x-auto">
                    {JSON.stringify(Object.fromEntries(Object.entries(selectedEvent.details).filter(([k]) => !['description', 'changed_fields'].includes(k))), null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
